import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Leaf, Users, Wind, Zap } from "lucide-react";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { heroScene, partnershipSlogan, profilePhoto, projects, testimonial } from "../data/content";
import { projectValue } from "../lib/transparency";
import { ResponsiveImage } from "./ResponsiveImage";
import { FourFronts } from "./FourFronts";

gsap.registerPlugin(useGSAP, ScrollTrigger);

export function HomeOverview() {
  const overview = useRef<HTMLElement>(null);
  const eolico = projects.find((project) => project.slug === "eolico")!;

  useGSAP(
    () => {
      const media = gsap.matchMedia();
      media.add("(prefers-reduced-motion: no-preference)", () => {
        gsap.timeline({
          scrollTrigger: { trigger: ".partnership", start: "top 72%", once: true },
          defaults: { ease: "power3.out", immediateRender: false },
        })
          .from("[data-partnership-copy] > *", { autoAlpha: 0, y: 30, stagger: 0.085, duration: 0.62 })
          .from("[data-partnership-photo]", { autoAlpha: 0, x: 58, duration: 0.85 }, 0.12)
          .from("[data-partnership-slogan]", { autoAlpha: 0, scale: 0.86, rotation: -2, duration: 0.68 }, 0.46);

        gsap.timeline({
          scrollTrigger: { trigger: ".wind-feature", start: "top 74%", once: true },
          defaults: { ease: "power3.out", immediateRender: false },
        })
          .from("[data-wind-copy] > *", { autoAlpha: 0, y: 28, stagger: 0.075, duration: 0.58 })
          .from("[data-wind-media]", { autoAlpha: 0, x: 55, clipPath: "inset(0 0 0 18%)", duration: 0.82 }, 0.12)
          .from("[data-wind-media] dl > div", { autoAlpha: 0, x: 22, stagger: 0.09, duration: 0.46 }, 0.5)
          .from("[data-wind-quote]", { autoAlpha: 0, y: 24, duration: 0.62 }, 0.7);
      });
      const refreshFrame = requestAnimationFrame(() => ScrollTrigger.refresh());
      let active = true;
      document.fonts?.ready.then(() => active && ScrollTrigger.refresh());
      return () => {
        active = false;
        cancelAnimationFrame(refreshFrame);
        media.revert();
      };
    },
    { scope: overview },
  );

  return (
    <section ref={overview} className="home-overview" aria-label="Parceria e investimentos em Palmas">
      <div className="partnership">
        <div className="partnership__decor" aria-hidden="true">
          <ResponsiveImage media={heroScene} priority alt="" sizes="100vw" />
        </div>
        <div className="partnership__slogan" data-partnership-slogan>
          <ResponsiveImage media={partnershipSlogan} priority sizes="(max-width: 560px) 150px, 240px" />
        </div>
        <div className="overview-shell partnership__grid">
          <div className="partnership__copy" data-partnership-copy>
            <p className="overview-kicker">Depoimento</p>
            <h2>Relato sobre<br />a relação com<br /><span>Palmas</span></h2>
            <blockquote className="partnership__body">{testimonial.text}</blockquote>
            <p className="partnership__author"><strong>{testimonial.name}</strong></p>
            <blockquote>“{testimonial.excerpt}”</blockquote>
          </div>
          <div className="partnership__photo" data-partnership-photo>
            <ResponsiveImage media={profilePhoto} priority sizes="(max-width: 800px) 100vw, 58vw" />
          </div>
        </div>
      </div>

      <FourFronts />

      <div className="wind-feature">
        <div className="overview-shell wind-feature__grid">
          <div className="wind-feature__copy" data-wind-copy>
            <p className="overview-kicker">INVESTIMENTO PRIVADO</p>
            <h2>Complexo Eólico<br />Palmas II</h2>
            <strong>{projectValue(eolico)}</strong>
            <small>Em investimento privado</small>
            <p>{eolico.summary}</p>
            <Link to="/investimentos#eolico">Saiba mais sobre o projeto <span aria-hidden="true">→</span></Link>
          </div>
          <div className="wind-feature__media" data-wind-media>
            <ResponsiveImage media={eolico.media[0]} sizes="(max-width: 800px) 100vw, 53vw" />
            <dl>
              <div><Wind aria-hidden="true" /><dt>{eolico.privateDetails?.turbines?.value}</dt><dd>aerogeradores</dd></div>
              <div><Zap aria-hidden="true" /><dt>{eolico.privateDetails?.capacity?.value}</dt><dd>potência instalada</dd></div>
              <div><Users aria-hidden="true" /><dt>{eolico.privateDetails?.jobs?.value}</dt><dd>estimativa de empregos</dd></div>
            </dl>
          </div>
        </div>
        <div className="overview-shell wind-feature__quote" data-wind-quote>
          <Leaf aria-hidden="true" />
          <strong>Complexo Eólico<br />Palmas II.</strong>
          <span>Investimento privado.<br />Não integra o total de recursos públicos.</span>
        </div>
      </div>
    </section>
  );
}
